import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { Flex, Typography } from 'antd';

import dayjs from 'dayjs';

import axios from '../../../axios';

import { setDeep } from '../../../helpers/common';

import messages from '../../../constants/messages';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { fetchRule } from '../../../redux/actions/rule';

import AppModal from '../../../components/AppModal';
import CancelSaveButtons from '../../../components/CancelSaveButtons';
import { errorToastHandler, warnToastHandler } from '../../../components/action_notifier';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';

import styles from './styles.module.scss';

import RuleForm from './RuleForm';
import RuleStep from './RuleStep';
import { TIME_BASED_RULE_TYPES } from './constants';

const { Title } = Typography;

function RuleBuilder({ history }) {
  const dispatch = useDispatch();

  const formRef = useRef(null);

  const { id } = useParams();

  const { rule, loading } = useSelector(state => state.rule);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    currentStep: 0,
    delete_modal: false,
    farms: [],
    new: id === 'new',
    rule: { type: null, farm: null, data: {} },
    steps: [],
    submittable: false,
    types: [],
    targets: []
  });

  useEffect(() => {
    if (!state.new) {
      getRule();
    }

    getFarms();
    getTypes();
    getOptions(state.rule);

    return () => {
      setState(prevState => ({ ...prevState, rule: { type: null, farm: null, data: {} } }));
      formRef.current?.resetFields();
    };
  }, []);

  useEffect(() => {
    generateDefinition(state.rule);
  }, [state.rule?.data]);

  useEffect(() => {
    if (rule && !state.new) {
      setState(prevState => ({
        ...prevState,
        //TODO: set to response data
        rule: {
          ...rule,
          type: {
            value: rule?.type,
            label: rule?.type_display_name
          },
          farm: {
            value: rule?.farm.id,
            label: rule?.farm.name
          }
        }
      }));
      getOptions({
        ...rule,
        type: {
          value: rule?.type,
          label: rule?.type_display_name
        },
        farm: {
          value: rule?.farm.id,
          label: rule?.farm.name
        }
      });
      generateDefinition(rule);
    }
  }, [rule]);

  const getFarms = async () => {
    const response = await axios.get('farms');
    const farms = response.data?.map(item => ({ value: item.id, label: item.name }));

    setState(prevState => ({ ...prevState, farms }));
  };

  const getTypes = async () => {
    let response = await axios.get('/rules/types');
    setState(prevState => ({
      ...prevState,
      types: response.data?.filter(
        i => !['gateway_disconnected', 'tag_disconnected', 'basic_health', 'intermediate_health'].includes(i.value)
      )
    }));
  };

  const getOptions = async rule => {
    if (!rule?.type?.value) return;

    const response = await axios.get('/rules/options', {
      params: {
        type: rule?.type?.value,
        farmId: rule?.farm?.value
      }
    });

    setState(prevState => ({
      ...prevState,
      rule: {
        ...prevState.rule,
        type: { value: response.data?.name, label: response.data?.display_name }
      }
    }));

    if (!response.data?.steps) return;

    setState(prevState => ({
      ...prevState,
      steps: response.data?.steps
    }));
  };

  const getRule = async () => {
    try {
      await dispatch(fetchRule({ id, params: { query: state.query } })).unwrap();
    } catch (error) {
      console.error(error);
    }
    // const response = await axios.get(`rules/${id}`, {
    //   query: state.query
    // });
  };

  const onFarmChange = (val, option) => {
    setState(prevState => ({
      ...prevState,
      rule: {
        ...(prevState?.rule || {}),
        type: {},
        farm: option
      },
      steps: [],
      currentStep: 1
    }));
  };

  const onTimeBasedChange = val => {
    setState(prevState => ({
      ...prevState,
      rule: {
        ...(prevState?.rule || {}),
        timeBased: val
      },
      currentStep: 2
    }));
  };

  const onTargetsChange = targets => {
    setState(prevState => ({ ...prevState, targets }));
  };

  const onRuleChange = (value, filed) => {
    setState(prevState => ({
      ...prevState,
      rule: {
        ...prevState.rule,
        [filed]: value
      }
    }));
  };

  const onTypeChange = async (sel, option) => {
    setState(prevState => ({
      ...prevState,
      rule: {
        ...prevState.rule,
        type: option,
        definition: 'Please complete some steps.',
        timeBased:
          prevState.rule?.timeBased !== undefined && TIME_BASED_RULE_TYPES.includes(option.value)
            ? prevState.rule.timeBased
            : false,
        data: {}
      },
      currentStep: TIME_BASED_RULE_TYPES.includes(option.value) ? 2 : 3
    }));

    formRef.current?.resetFields();
    formRef.current?.setFieldsValue({
      farm: state.rule?.farm,
      type: option,
      name: state.rule?.name,
      description: state.rule?.description
    });

    await getOptions({
      ...state.rule,
      type: option,
      definition: 'Please complete some steps.'
    });
  };

  const onStepChange = (value, field) => {
    setState(prevState => {
      const data = { ...(prevState.rule?.data || {}) };

      let normalizedValue = value;
      if (value && typeof value === 'object' && value.isValid && typeof value.format === 'function') {
        normalizedValue = value.format('HH:mm');
      }

      if (field.includes('.')) {
        setDeep(data, field, normalizedValue);
      } else {
        data[field] = normalizedValue;
      }

      return { ...prevState, rule: { ...prevState.rule, data } };
    });
  };

  const toggleModal = modal => {
    setState(prevState => ({
      ...prevState,
      [modal]: !prevState[modal]
    }));
  };

  const hhmmToMinutes = hhmm => {
    const [h, m] = hhmm.split(':').map(Number);

    return h * 60 + m;
  };

  const composeRuleTargetsToCreate = (ruleType, targets = []) => {
    if (!TIME_BASED_RULE_TYPES.includes(ruleType)) {
      return [];
    }

    return targets.map(({ target, startTime, endTime, scheduleVariance }) => ({
      geofenceId: target,
      startTime: startTime.format('HH:mm'),
      endTime: endTime.format('HH:mm'),
      scheduleVariance: hhmmToMinutes(scheduleVariance.format('HH:mm')) || 0
    }));
  };

  const saveRule = async () => {
    let response;

    try {
      const ruleType = state.rule.type.value;

      if (state.new) {
        response = await axios.post('rules', {
          ...state.rule,
          farm_id: state.rule.farm.value,
          type: state.rule.type.value,
          timeBased: state.rule.timeBased,
          targets: composeRuleTargetsToCreate(ruleType, state.targets)
        });
      } else {
        response = await axios.put(`rules/${id}`, {
          ...state.rule,
          farm_id: state.rule.farm.value,
          type: state.rule.type.value,
          timeBased: state.rule.timeBased,
          targets: composeRuleTargetsToCreate(ruleType, state.targets)
        });
      }

      if (response.status === 200) {
        setIsFormDirty(false);
        history.push(`/rule/${response.data?.id}`);
      }
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_CREATE_DATA);
    }
  };

  const generateDefinition = rule => {
    const def = [];
    const data = rule ? rule.data : state.rule?.data;
    const type = rule ? rule.type?.value : state.rule?.type?.value;
    const not_defined = <span>{''}</span>;

    if (type === 'intermediate_health') {
      def.push(<span>Any animal moves less than </span>);

      if (data?.variables) {
        if (data?.variables?.percentage) {
          def.push(
            <span>
              <span className={styles.bolded}>{data?.variables?.percentage}%</span>
              {' of '}
            </span>
          );
        }

        if (data?.variables?.calculated_value) {
          def.push(
            <span>
              <span className={styles.bolded}>{data?.variables?.calculated_value?.value}</span>{' '}
            </span>
          );
        }
      }
      if (data?.action) {
        def.push(
          <span>
            {'. If rule breached '}
            {data?.action?.label}{' '}
          </span>
        );
      } else {
        def.push(not_defined);
      }

      setState(prevState => ({ ...prevState, rule: { ...prevState.rule, definition: def } }));

      return;
    }

    if (data?.selector) {
      def.push(
        <span>
          <span className={styles.bolded}>{data?.selector.label}</span>
          {'\u00A0'}livestock labeled{'\u00A0'}
        </span>
      );
    } else {
      def.push(not_defined);
    }

    if (data?.anchor) {
      def.push(
        <span className={styles.bolded}>
          {data?.anchor.label}
          {'\u00A0'}
        </span>
      );
    } else {
      def.push(not_defined);
    }

    if (data?.condition) {
      def.push(<span>{data?.condition.label} </span>);
    } else {
      def.push(not_defined);
    }

    if (type === 'site_proximity' || type === 'livestock_proximity' || type === 'basic_health') {
      if (data?.variables) {
        if (data?.variables.distance) {
          def.push(
            <span className={styles.bolded}>
              {'\u00A0'}
              {data?.variables.distance}
              {'\u00A0'}
            </span>
          );
        } else {
          def.push(not_defined);
        }
        if (data?.variables.distance_unit) {
          def.push(
            <span>
              <span className={styles.bolded}>{data?.variables.distance_unit.label}</span>
              {'\u00A0'}
              {type !== 'basic_health' && 'of'}
              {'\u00A0'}
            </span>
          );
        } else {
          def.push(not_defined);
        }
        if (type === 'basic_health') {
          if (data?.variables.timeframe) {
            def.push(
              <span className={styles.bolded}>
                {'\u00A0'}
                {data?.variables.timeframe.label}
                {'\u00A0'}
              </span>
            );
          } else {
            def.push(not_defined);
          }
        }
      }
    }

    if (data?.target) {
      def.push(
        <span className={styles.bolded}>
          {'\u00A0'}
          {data?.target.label}
          {'\u00A0'}
        </span>
      );
    }

    if (type === 'site_frequency') {
      if (data?.variables) {
        if (data?.variables.frequency) {
          def.push(
            <span>
              <span className={styles.bolded}>{data?.variables.frequency} </span> {' times '}
            </span>
          );
        } else {
          def.push(not_defined);
        }
        if (data?.variables.timeframe) {
          def.push(
            <span className={styles.bolded}>
              {'\u00A0'}
              {data?.variables.timeframe.label}
            </span>
          );
        } else {
          def.push(not_defined);
        }
      }
    }

    if (data?.action) {
      def.push(
        <span>
          {'. If rule breached '}
          {data?.action.label}{' '}
        </span>
      );
    } else {
      def.push(not_defined);
    }

    setState(prevState => ({ ...prevState, rule: { ...prevState.rule, definition: def } }));
  };

  const deleteRule = async () => {
    await axios.delete(`rules/${id}`);
    history.push('/rules');
  };

  const breadcrumbItems = [
    {
      title: <Link to="/rules">List of Rules</Link>
    },
    {
      title: `Rule ${state.rule.name || ''}`
    }
  ];

  const handleCancel = useCallback(() => {
    history.push(state.new ? '/rules' : `/rule/${id}`);
  }, []);

  const { NavigationPrompt, modalProps, nextLocation, confirmed } = useUnsavedChangesPrompt({
    when: isFormDirty,
    onSaveAndLeave: async () => {
      if (submittable) {
        await saveRule();

        return true;
      } else {
        warnToastHandler(messages.DATA_REQUIRED);
        formRef.current?.validateFields();

        return false;
      }
    },
    onDiscardAndLeave: () => {
      formRef.current?.resetFields();
      setIsFormDirty(false);
    }
  });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  return (
    <>
      {NavigationPrompt}
      <Flex gap="small" vertical={false} justify="space-between" wrap>
        <Flex vertical={true}>
          <Title level={4}>Rule {state.rule.name}</Title>

          <AppBreadcrumb items={breadcrumbItems} />
        </Flex>
        <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={saveRule} />
      </Flex>

      {!loading && (
        <RuleForm
          currentStep={state.currentStep}
          farms={state.farms}
          formRef={formRef}
          isNew={state.new}
          rule={state.rule}
          steps={state.steps}
          onStepChange={onStepChange}
          types={state.types}
          onChange={setIsFormDirty}
          onFarmChange={onFarmChange}
          onRuleChange={onRuleChange}
          onTypeChange={onTypeChange}
          onTimeBasedChange={onTimeBasedChange}
          onTargetsChange={onTargetsChange}
          setSubmittable={setSubmittable}
        />
      )}

      <Flex justify="end">
        <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={saveRule} />
      </Flex>

      <AppModal
        isOpen={state.delete_modal}
        confirmButtonColor="danger"
        confirmButtonText="Archive"
        title="Archive rule"
        handleCancel={() => toggleModal('delete_modal')}
        handleConfirm={deleteRule}>
        <div className="py-4">{messages.ARCHIVE_CONFIRMATION('rule')}</div>
      </AppModal>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </>
  );
}

export default RuleBuilder;
